// src/modules/operaciones/inventarios/hooks/useDashboard.js
//
// Estado del dashboard de informes: filtros globales, pestaña activa y mapeo manual de columnas.
// Recalcula el análisis con useMemo cada vez que cambian los datos, los filtros o el mapeo.
//
// USO
//   const d = useDashboard(parseado);
//   d.analisis                         → resultado de analizarDatos (null si no hay datos)
//   d.esMermas                         → true si es el dashboard de mermas
//   d.pestana / d.setPestana("bodegas")
//   d.pestanasDisponibles              → [{ id, etiqueta }]
//   d.filtros / d.setFiltro("bodegas", ["SAN JORGE"]) / d.toggleFiltro("bodegas", "SAN JORGE")
//   d.limpiarFiltros() / d.cantidadFiltros / d.hayFiltros
//   d.mapeoManual / d.setMapeo("fabricanteCol", "col3") / d.restablecerMapeo()
//   d.roles / d.catalogos / d.ventaNoFiltrable / d.error
//
// NOTAS
//  - `datos` es el `parseado` del backend (no el análisis): se necesita la tabla original para
//    recalcular. Si el informe guardado no la trae, `analisis` será null y el viewer debe usar
//    el resultado guardado.
//  - El formato largo (`largo`) se desactiva por defecto: pesa y ningún panel lo necesita.
//    Pasa { incluirLargo: true } si lo quieres.
//  - Al cambiar `datos` (otro archivo) o el mapeo de columnas, los filtros se reinician, porque
//    los catálogos (bodegas, fabricantes...) pueden ser distintos.

import { useCallback, useEffect, useMemo, useState } from "react";
import { analizarDatos } from "../utils/analizarDatos";

/* ========================================================================== */
/* Constantes                                                                 */
/* ========================================================================== */

export const PESTANAS = [
  { id: "resumen", etiqueta: "Resumen" },
  { id: "bodegas", etiqueta: "Bodegas" },
  { id: "fabricantes", etiqueta: "Fabricantes" },
  { id: "articulos", etiqueta: "Artículos" },
  { id: "alertas", etiqueta: "Alertas" },
  { id: "datos", etiqueta: "Datos" },
];

// En modo genérico (otros Excel) solo existe el resumen.
const PESTANAS_GENERICO = PESTANAS.filter((p) => p.id === "resumen");

// Filtros de selección (listas vacías = todos) y casillas.
const CLAVES_LISTA = ["bodegas", "tipos", "fabricantes", "familias"];
const CLAVES_CASILLA = ["soloConMerma", "ocultarSinMovimiento"];

export const FILTROS_INICIALES = Object.freeze({
  bodegas: [],
  tipos: [],
  fabricantes: [],
  familias: [],
  soloConMerma: false,
  ocultarSinMovimiento: false,
  // Ajustes de cálculo (no cuentan como "filtros activos")
  nivelFaltantes: "celda", // "celda" | "articulo"
  umbralMermaPct: 5,
});

const MAPEO_INICIAL = Object.freeze({});

/* ========================================================================== */
/* Hook                                                                       */
/* ========================================================================== */

export function useDashboard(datos, { pestanaInicial = "resumen", incluirLargo = false } = {}) {
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [pestanaElegida, setPestanaElegida] = useState(pestanaInicial);
  const [mapeoManual, setMapeoManual] = useState(MAPEO_INICIAL);

  // Otro archivo → vuelve todo al inicio
  useEffect(() => {
    setFiltros(FILTROS_INICIALES);
    setMapeoManual(MAPEO_INICIAL);
    setPestanaElegida(pestanaInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos]);

  /* ---------- Análisis (se recalcula con filtros y mapeo) ---------- */
  const hayMapeo = Object.keys(mapeoManual).length > 0;

  const { analisis, error } = useMemo(() => {
    if (!datos) return { analisis: null, error: null };
    try {
      const resultado = analizarDatos(
        datos,
        { ...filtros, incluirLargo },
        { mapeoManual: hayMapeo ? mapeoManual : null }
      );
      return { analisis: resultado, error: null };
    } catch (e) {
      console.error("Error al analizar el informe:", e);
      return { analisis: null, error: e };
    }
  }, [datos, filtros, mapeoManual, hayMapeo, incluirLargo]);

  const esMermas = Boolean(analisis?.esMermas);

  /* ---------- Pestañas ---------- */
  const pestanasDisponibles = esMermas ? PESTANAS : PESTANAS_GENERICO;
  // Si la pestaña elegida no existe en este modo, se muestra la primera.
  const pestana = pestanasDisponibles.some((p) => p.id === pestanaElegida)
    ? pestanaElegida
    : pestanasDisponibles[0].id;

  const setPestana = useCallback((id) => setPestanaElegida(id), []);

  /* ---------- Filtros ---------- */
  // setFiltro("bodegas", ["SAN JORGE"])  |  setFiltro("soloConMerma", true)
  const setFiltro = useCallback((clave, valor) => {
    setFiltros((prev) => {
      if (!Object.prototype.hasOwnProperty.call(FILTROS_INICIALES, clave)) return prev;
      if (prev[clave] === valor) return prev;
      return { ...prev, [clave]: valor };
    });
  }, []);

  // Agrega o quita un elemento de una lista: toggleFiltro("fabricantes", "Italcol")
  const toggleFiltro = useCallback((clave, valor) => {
    if (!CLAVES_LISTA.includes(clave)) return;
    setFiltros((prev) => {
      const actual = prev[clave];
      const siguiente = actual.includes(valor) ? actual.filter((v) => v !== valor) : [...actual, valor];
      return { ...prev, [clave]: siguiente };
    });
  }, []);

  // Quita todos los filtros; conserva los ajustes de cálculo (nivelFaltantes, umbral).
  const limpiarFiltros = useCallback(() => {
    setFiltros((prev) => ({
      ...FILTROS_INICIALES,
      nivelFaltantes: prev.nivelFaltantes,
      umbralMermaPct: prev.umbralMermaPct,
    }));
  }, []);

  const cantidadFiltros = useMemo(
    () =>
      CLAVES_LISTA.filter((k) => filtros[k].length > 0).length +
      CLAVES_CASILLA.filter((k) => filtros[k]).length,
    [filtros]
  );

  /* ---------- Mapeo manual de columnas ---------- */
  // setMapeo("fabricanteCol", "col3")  → fuerza esa columna
  // setMapeo("fabricanteCol", null)    → deja el rol vacío
  // setMapeo("fabricanteCol", undefined) → vuelve a la detección automática de ese rol
  const setMapeo = useCallback((rol, columnaId) => {
    setMapeoManual((prev) => {
      const siguiente = { ...prev };
      if (columnaId === undefined) delete siguiente[rol];
      else siguiente[rol] = columnaId;
      return siguiente;
    });
    // Los catálogos pueden cambiar con otro mapeo: se reinician los filtros.
    setFiltros((prev) => ({
      ...FILTROS_INICIALES,
      nivelFaltantes: prev.nivelFaltantes,
      umbralMermaPct: prev.umbralMermaPct,
    }));
  }, []);

  const restablecerMapeo = useCallback(() => {
    setMapeoManual(MAPEO_INICIAL);
    setFiltros((prev) => ({
      ...FILTROS_INICIALES,
      nivelFaltantes: prev.nivelFaltantes,
      umbralMermaPct: prev.umbralMermaPct,
    }));
  }, []);

  return {
    // Resultado
    analisis,
    esMermas,
    error,
    roles: analisis?.roles || null,
    catalogos: analisis?.catalogos || null,
    // La venta no se puede separar por bodega: con filtro de bodega/tipo el % merma/venta es aproximado
    ventaNoFiltrable: Boolean(analisis?.metaAnalisis?.ventaNoFiltrable),

    // Pestañas
    pestana,
    setPestana,
    pestanasDisponibles,

    // Filtros
    filtros,
    setFiltro,
    toggleFiltro,
    limpiarFiltros,
    cantidadFiltros,
    hayFiltros: cantidadFiltros > 0,

    // Mapeo manual
    mapeoManual,
    hayMapeo,
    setMapeo,
    restablecerMapeo,
  };
}

export default useDashboard;